// hooks/useDateFormatter.ts
"use client";

import { useLocale } from "next-intl";
import { useMemo } from "react";

/**
 * Single source of truth for the timezone used to format dates.
 * MUST be identical on the server (Node) and the client (browser),
 * otherwise React will throw a hydration mismatch.
 *
 * Pick the timezone your content is authored in:
 *   - "UTC"          → server-friendly, deterministic everywhere
 *   - "Asia/Tehran"  → matches the audience if posts are Tehran-local
 */
const TIME_ZONE = "Asia/Tehran";

export function useDateFormatter() {
    const locale = useLocale();
    const dateLocale = locale === "fa" ? "fa-IR" : "en-US";

    // Cache formatter instances per (locale, options) so repeated calls
    // in the same render are cheap and referentially stable.
    const dateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(dateLocale, {
                timeZone: TIME_ZONE,
                year: "numeric",
                month: "numeric",
                day: "numeric",
                calendar: locale === "fa" ? "persian" : "gregory",
            }),
        [dateLocale, locale],
    );

    const dateTimeFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(dateLocale, {
                timeZone: TIME_ZONE,
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                calendar: locale === "fa" ? "persian" : "gregory",
            }),
        [dateLocale, locale],
    );

    /**
     * Format a date-only string. Always uses TIME_ZONE so SSR and CSR agree.
     * Custom `options` cannot override `timeZone` — we force it after spreading.
     */
    const formatDate = (
        date: Date | string,
        options?: Intl.DateTimeFormatOptions,
    ) => {
        const dateObj = typeof date === "string" ? new Date(date) : date;
        if (options) {
            return new Intl.DateTimeFormat(dateLocale, {
                calendar: locale === "fa" ? "persian" : "gregory",
                ...options,
                timeZone: TIME_ZONE, // force, even if caller passed one
            }).format(dateObj);
        }
        return dateFormatter.format(dateObj);
    };

    /**
     * Format a date + time. Same rules as formatDate.
     */
    const formatDateTime = (
        date: Date | string,
        options?: Intl.DateTimeFormatOptions,
    ) => {
        const dateObj = typeof date === "string" ? new Date(date) : date;
        if (options) {
            return new Intl.DateTimeFormat(dateLocale, {
                calendar: locale === "fa" ? "persian" : "gregory",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                ...options,
                timeZone: TIME_ZONE,
            }).format(dateObj);
        }
        return dateTimeFormatter.format(dateObj);
    };

    /**
     * Escape hatch: format in an explicit timezone.
     * Only safe in client-only code paths (e.g. inside useEffect),
     * because the server can't know the user's zone.
     */
    const formatDateTimeWithTimezone = (
        date: Date | string,
        timezone: string,
        options?: Intl.DateTimeFormatOptions,
    ) => {
        const dateObj = typeof date === "string" ? new Date(date) : date;
        return new Intl.DateTimeFormat(dateLocale, {
            timeZone: timezone,
            calendar: locale === "fa" ? "persian" : "gregory",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            ...options,
        }).format(dateObj);
    };

    return {
        formatDate,
        formatDateTime,
        formatDateTimeWithTimezone,
        currentLocale: dateLocale,
        timeZone: TIME_ZONE,
    };
}